import React from 'react'

/**
 * Australian phone number patterns (for linkify on AU contact page).
 * Matches: 1300 xxx xxx, 1800 xxx xxx, (0x) xxxx xxxx, 0x xxxx xxxx.
 */
const AU_PHONE_REGEX =
  /(?:1[38]00[\s.-]*\d{3}[\s.-]*\d{3}|(?:\(?0?[2-478]\)?[\s.-]*)\d{4}[\s.-]*\d{4})/g

/**
 * Normalise a matched phone string to E.164-style for tel: href (digits only, AU with +61).
 */
function normaliseTelHref(raw: string): string {
  const digits = raw.replace(/\D/g, '')
  if (digits.length === 0) return raw
  if (raw.trimStart().startsWith('+61') || digits.startsWith('61')) {
    return `+${digits.replace(/^61/, '61')}`
  }
  if (digits.startsWith('0')) {
    return `+61${digits.slice(1)}`
  }
  // 1300 and 1800 numbers are special service numbers that don't use country codes
  if (
    digits.startsWith('1') &&
    (digits.startsWith('13') || digits.startsWith('1800') || digits.startsWith('1300'))
  ) {
    return digits
  }
  if (digits.length >= 9) {
    return `+61${digits}`
  }
  return `+61${digits}`
}

/**
 * Splits text by AU phone patterns and returns React nodes with phone numbers wrapped in tel: links.
 * Used for AU contact page so plain-text phone numbers become clickable.
 */
export function linkifyPhoneNumbers(text: string): React.ReactNode {
  if (!text || typeof text !== 'string') return text

  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  AU_PHONE_REGEX.lastIndex = 0
  while ((match = AU_PHONE_REGEX.exec(text)) !== null) {
    const fullMatch = match[0]
    const before = text.slice(lastIndex, match.index)
    if (before) parts.push(before)
    const tel = normaliseTelHref(fullMatch)
    parts.push(
      <a key={match.index} href={`tel:${tel}`}>
        {fullMatch}
      </a>
    )
    lastIndex = match.index + fullMatch.length
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  if (parts.length === 0) return text
  if (parts.length === 1) return parts[0]
  return <>{parts}</>
}
